/**
 * The interpretation profile carried on an EDI Guideline.
 *
 * A guideline is a vendor's document; this is the small structured part of it
 * that changes what the system does. It states what *this* vendor's codes mean
 * in the places where vendors legitimately differ, and it is applied to a
 * document by resolving the profile of the partner who sent it.
 *
 * **It never forks the parser.** There is one parser per transaction set, both
 * known vendors' 6,947 corpus documents read through it correctly, and a
 * per-vendor parser module was tried and abandoned on #6556. Everything here
 * adjusts how a parse is *interpreted*; nothing here changes how bytes are
 * read. A vendor sending something the standard parser genuinely cannot read is
 * a parser fix for everyone, not a configuration surface.
 *
 * **Every field is optional, and absent means "the vendor did not say".** A
 * profile is not a schema the vendor must fill in — it is a record of what
 * their guideline documents. Where it says nothing, the standard reading
 * applies unchanged, exactly as it does for a partner holding no guideline at
 * all. That is what makes filling one in safe: a half-written profile cannot
 * be worse than none.
 *
 * **What is deliberately not here.** Two fields would be obvious additions and
 * are left out for the same reason — a field nothing reads is a field that
 * drifts, so each waits for the consumer that would read it.
 *
 * The role code identifying a *branch* (`N101`) is documented differently by
 * each vendor, but nothing consumes it yet: `mapEdi845ToSpa` records every
 * party with its raw role code and leaves the branch judgement to whatever
 * later needs an answer, and `pricing_spa_parties.resolved_branch_id` — where
 * such a judgement would land — is not populated by this path.
 *
 * **Which `LIN` qualifier carries the catalogue number** is the other, and it
 * is the nearer of the two. Vendors genuinely differ: a Rockwell SKU line
 * states `UK` (UPC-14), `UI` and `VP`, where Schneider states `VC` and then
 * `UI` or `EN`. Every pair is recorded verbatim on the line's
 * `productIdentifiers`, and `productNumber` is the first — which already means
 * a UPC for one vendor and a catalogue number for the other. A
 * `productIdentifierQualifiers` field here is where that is settled, per
 * vendor and with no deploy, on the day something joins an 845 line to an Item
 * Master. Nothing does today, so choosing now would be choosing blind; the
 * choice stays open because every identifier is stored. See
 * `docs/pricing/open-questions.md`, "Which identifier is the catalogue
 * number?".
 */

import { z } from "zod";
import { SpaQuoteType } from "./pricing";

/**
 * Which CTP element this vendor states its class of trade in.
 *
 * Rockwell uses `CTP01` (`DI`, `RS`); Schneider leaves that empty and uses
 * `CTP02` (`SHD`). Both already work without a profile, because the mapper
 * reads whichever one the sender populated — this exists to make the *stated*
 * position authoritative for a vendor who populates both, where reading the
 * wrong one would silently mislabel a price tier.
 */
export const Edi845ClassOfTradeElementSchema = z.enum(["CTP01", "CTP02"]);
export type Edi845ClassOfTradeElement = z.infer<
  typeof Edi845ClassOfTradeElementSchema
>;

/**
 * What a `PAD02` code resolves a SPA's agreement type to.
 *
 * `SpaQuoteType` itself rather than a second list of the same five values: what
 * a profile states here is what lands in `pricing_spas.quote_type`, so a value
 * this accepts and that column does not would be read as "Other" with nothing
 * saying why. One list, and the coupling is the point.
 */
export const EdiGuidelineQuoteTypeSchema = z.nativeEnum(SpaQuoteType);
export type EdiGuidelineQuoteType = SpaQuoteType;

/** The same values as a list, for a picker to render. */
export const EDI_GUIDELINE_QUOTE_TYPES = Object.values(SpaQuoteType);

/**
 * One `PAD02` product-transfer-type code, as the vendor's guideline defines it.
 *
 * `label` is the vendor's own wording, kept so a reader can see what was
 * claimed rather than only its consequence. `quoteType` is the consequence: the
 * agreement type a document carrying this code authorises.
 *
 * A code the vendor's guideline does not define is **absent from this map**,
 * and that absence is the point. 19,806 line items carry `PAD02 = SS`, which
 * Square D's own guideline does not define, and every one of them is currently
 * labelled ship-and-debit on that assumption. Leaving `SS` out of the map is
 * what stops us asserting something the vendor never said; naming it is a
 * separate question for the vendor to answer.
 */
export const Edi845ProductTransferTypeSchema = z.object({
  label: z.string().min(1).max(160),
  quoteType: EdiGuidelineQuoteTypeSchema,
});
export type Edi845ProductTransferType = z.infer<
  typeof Edi845ProductTransferTypeSchema
>;

/**
 * Which rung of a vendor's hierarchy a line prices at.
 *
 * The two the domain distinguishes, and no more: an individual catalogue item,
 * or a grouping above it — see `docs/pricing/CONTEXT.md`, "Price Level". A
 * vendor's own rungs are finer than that (Rockwell has five, and states two of
 * them in `PID04`), and the code it sent is kept verbatim on the line beside
 * this, so naming the rung is not the same as flattening it away.
 */
export const Edi845PriceLevelSchema = z.enum(["Item", "ProductGroup"]);
export type Edi845PriceLevel = z.infer<typeof Edi845PriceLevelSchema>;

/** The same values as a list, for a picker to render. */
export const EDI_GUIDELINE_PRICE_LEVELS = Edi845PriceLevelSchema.options;

/**
 * One `PID04` product-description code, as the vendor's guideline defines it.
 *
 * The same shape as {@link Edi845ProductTransferTypeSchema} and for the same
 * reasons: `label` is the vendor's own wording, kept so a reader can see what
 * was claimed rather than only its consequence, and `priceLevel` is the
 * consequence.
 *
 * A code the vendor does not define is **absent from this map**, and the
 * absence is the point — the line's price level is then recorded as undeclared
 * and flagged, never inferred from the shape of the product code.
 */
export const Edi845ProductDescriptionCodeSchema = z.object({
  label: z.string().min(1).max(160),
  priceLevel: Edi845PriceLevelSchema,
});
export type Edi845ProductDescriptionCode = z.infer<
  typeof Edi845ProductDescriptionCodeSchema
>;

/**
 * An X12 code as a map key: short, no punctuation, no whitespace.
 *
 * Exported because a form editing a profile has to refuse the same keys this
 * refuses, and a second copy of the pattern there would be a rule that agrees
 * with this one only until one of them is edited.
 */
export const EdiGuidelineCodeKeySchema = z
  .string()
  .regex(/^[A-Za-z0-9]{1,10}$/, {
    message: "An EDI code key must be 1-10 letters or digits.",
  });

/**
 * The whole profile. Empty (`{}`) is valid and means "we hold this vendor's
 * guideline but have recorded nothing structured from it yet" — which reads
 * exactly as the standard behaviour does, while still recording that the
 * document was read against a guideline.
 */
export const EdiGuidelineInterpretationSchema = z.object({
  classOfTradeElement: Edi845ClassOfTradeElementSchema.optional(),
  /**
   * `PAD02` code → what it means. Keyed by the code as the vendor transmits
   * it; lookups are case-insensitive so a guideline recorded as `sd` still
   * matches a document sending `SD`.
   */
  productTransferTypes: z
    .record(EdiGuidelineCodeKeySchema, Edi845ProductTransferTypeSchema)
    .optional(),
  /**
   * `CON03` contract-status codes the vendor's guideline documents, code →
   * the vendor's own wording. Square D documents three: `OC` original, `CM`
   * modified, `EX` expired.
   *
   * The status is still stored verbatim whatever this says — a vendor sends
   * what they send, and defaulting it away would be unrecoverable. This only
   * decides whether the code they sent is one they told us about.
   */
  contractStatusCodes: z
    .record(EdiGuidelineCodeKeySchema, z.string().min(1).max(160))
    .optional(),
  /**
   * `PID04` code -> the price level it declares. Keyed and looked up exactly as
   * {@link productTransferTypes} is, case-insensitively.
   *
   * Absent means the vendor never told us what their product description codes
   * mean, which is not the same claim as "this vendor defines no such code":
   * the first leaves every line's price level simply unasked, the second flags
   * each line the vendor sent a code on.
   */
  productDescriptionCodes: z
    .record(EdiGuidelineCodeKeySchema, Edi845ProductDescriptionCodeSchema)
    .optional(),
});

export type EdiGuidelineInterpretation = z.infer<
  typeof EdiGuidelineInterpretationSchema
>;

/**
 * A stored profile, parsed.
 *
 * The column is `jsonb`, so what is in a row is only what some earlier write
 * put there, and every reader has to decide what to do about a profile that no
 * longer parses. They decide differently on purpose — the API reports it as no
 * profile, the ingest path raises rather than silently relabelling a vendor's
 * documents, the write refuses — so the *policy* stays at each call site and
 * only the parse is shared. Three copies of the parse is how the three would
 * come to disagree about what a valid profile is.
 */
export function parseEdiGuidelineInterpretation(value: unknown) {
  return EdiGuidelineInterpretationSchema.safeParse(value ?? {});
}

/**
 * A vendor key, brought to the one shape it is compared in: lowercase, letters
 * and digits only.
 *
 * The key an inbound document arrives under is derived from an email subject
 * by machine; the key a company declares is typed by a person reading a
 * vendor's name off that same email. They meet only if both are normalized the
 * same way, so this is the single statement of how — used by the write that
 * stores a declared key and by the resolver that compares one, which is what
 * stops "Schneider" and "schneider" from being two different vendors.
 */
export function normalizeEdiVendorKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * One declared vendor key. Normalized rather than rejected for case or
 * punctuation — a person typing `Square-D` means `squared` — but a value that
 * normalizes to nothing at all is refused, because it would match every
 * document or none depending on which side of the comparison it landed.
 */
export const EdiVendorKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(60)
  .transform(normalizeEdiVendorKey)
  .refine((key) => key.length > 0, {
    message: "A vendor key must contain a letter or a digit.",
  });
